import { useState, useEffect } from "react";
import {
  ShieldCheck,
  Award,
  FileCheck,
  Leaf,
  Lock,
  BadgeCheck,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

const iconMap = {
  1: ShieldCheck,
  2: Award,
  3: BadgeCheck,
  4: FileCheck,
  5: Leaf,
  6: Lock,
};

// Fallback certifications
const fallbackCertifications = [
  { id: "1", name: "Licensed & Insured", description: "Fully licensed painting contractor in Florida" },
  { id: "2", name: "EPA Lead-Safe", description: "EPA certified for lead-safe work practices" },
  { id: "3", name: "BBB A+ Rating", description: "Better Business Bureau accredited" },
  { id: "4", name: "5-Star Rated", description: "Top-rated on Google, Yelp & Angi" },
  { id: "5", name: "Warranty Included", description: "Written warranty on all painting work" },
  { id: "6", name: "Free Estimates", description: "No-obligation quotes for every project" },
];

export const Certifications = () => {
  const [certifications, setCertifications] = useState(fallbackCertifications);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCertifications = async () => {
      if (!API) return; // No backend, use fallback data already set
      try {
        const response = await axios.get(`${API}/certifications`);
        if (response.data.certifications?.length) {
          setCertifications(response.data.certifications);
        }
      } catch (error) {
        // Keep using fallback data
        console.log("Using fallback certifications data");
      }
    };
    fetchCertifications();
  }, []);

  return (
    <section
      data-testid="certifications-section"
      className="py-16 bg-brand-ice border-y border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">
            Trust & Quality
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight uppercase text-brand-navy">
            Certifications & Licensing
          </h2>
        </div>

        {/* Certification Badges Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {certifications.map((cert) => {
            const Icon = iconMap[cert.id] || ShieldCheck;
            return (
              <div
                key={cert.id}
                data-testid={`certification-${cert.id}`}
                className="group flex flex-col items-center text-center p-4 bg-white rounded-sm border border-border hover:border-brand-orange/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-brand-navy/5 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-orange/10 transition-colors duration-300">
                  <Icon className="w-8 h-8 text-brand-navy group-hover:text-brand-orange transition-colors duration-300" />
                </div>
                <h3 className="font-heading text-sm font-bold uppercase text-brand-navy leading-tight">
                  {cert.name}
                </h3>
                <p className="text-xs text-brand-slate mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {cert.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Trust Statement */}
        <div className="text-center mt-12 pt-8 border-t border-border">
          <p className="text-brand-slate text-sm max-w-2xl mx-auto">
            All licenses and certifications are current and available for
            verification upon request.
            <br />
            <span className="text-brand-orange font-medium">
              License numbers will be updated soon.
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};
